import React, { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaCrown, FaStar, FaGem, FaPhone, FaVideo, FaEnvelope, FaHeart, FaSearch, FaChartLine, FaBolt, FaShieldAlt, FaHeadset, FaUserTie, FaInfinity, FaEye } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';

const Subscription = () => {
    const [plans, setPlans] = useState([]);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [loading, setLoading] = useState(true);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [usageStats, setUsageStats] = useState(null);

    // Message system
    const [messages, setMessages] = useState([]);

    const showMessage = (type, text) => {
        const id = Date.now();
        setMessages(prev => [...prev, { id, type, text }]);
        setTimeout(() => {
            setMessages(prev => prev.filter(m => m.id !== id));
        }, 5000);
    };

    useEffect(() => {
        fetchPlans();
        fetchCurrentSubscription();
        fetchUsageStats();
        verifyPaymentOnReturn(); // Check if user returned from payment
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await api.get('/subscription/plans');
            setPlans(response.data);
        } catch (error) {
            console.error('Error fetching plans:', error);
            showMessage('error', 'Failed to load subscription plans.');
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrentSubscription = async () => {
        try {
            const response = await api.get('/subscription/my-subscription');
            setCurrentSubscription(response.data);
        } catch (error) {
            console.error('Error fetching subscription:', error);
        }
    };

    const fetchUsageStats = async () => {
        try {
            const response = await api.get('/subscription/usage/stats');
            setUsageStats(response.data);
        } catch (error) {
            console.error('Error fetching usage stats:', error);
        }
    };

    // Verify payment when user returns from gateway
    const verifyPaymentOnReturn = async () => {
        const urlParams = new URLSearchParams(window.location.search);
        let merchantOrderId = urlParams.get('merchantOrderId') ||
            localStorage.getItem('pendingSubscriptionOrderId');

        if (!merchantOrderId) return;

        // Prevent double verification
        if (localStorage.getItem('paymentVerified') === merchantOrderId) return;

        setLoading(true);
        try {
            const planName =
                urlParams.get('planName') || localStorage.getItem('pendingPlanName');
            const billingCycle =
                urlParams.get('billingCycle') || localStorage.getItem('pendingBillingCycle');

            const response = await api.get(
                `/subscription/verify-payment/${merchantOrderId}`,
                {
                    params: { planName, billingCycle }
                }
            );


            if (response.data.success) {
                showMessage('success', 'Payment successful! Your premium plan is now active.');
                await fetchCurrentSubscription();
                await fetchUsageStats();

                localStorage.setItem('paymentVerified', merchantOrderId);
                localStorage.removeItem('pendingSubscriptionOrderId');

                // Clean URL
                window.history.replaceState({}, '', '/subscription');
            } 
        } catch (error) {
            console.error('Payment verification failed:', error);
            showMessage('error', 'Could not verify payment status. Please contact support if charged.');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectPlan = (plan) => {
        if (plan.name === 'free') {
            showMessage('info', 'You are already on the free plan.');
            return;
        }
        setSelectedPlan(plan);
        setShowPaymentModal(true);
    };

   const handleSubscribe = async () => {
    if (!selectedPlan) return;

    setPaymentProcessing(true);

    try {
        const redirectUrl = `${window.location.origin}/dashboard`;

        const response = await api.post('/subscription/create-order', {
            planName: selectedPlan.name,
            billingCycle,
            redirectUrl,
        });

        const { checkoutUrl, merchantOrderId } = response.data;

        if (!checkoutUrl || !merchantOrderId) {
            throw new Error('Invalid payment link received');
        }

        // ✅ CLOSE MODAL IMMEDIATELY
        setShowPaymentModal(false);
        setSelectedPlan(null);

        // Save for verification
        localStorage.setItem('pendingSubscriptionOrderId', merchantOrderId);
        localStorage.setItem('pendingPlanName', selectedPlan.name);
        localStorage.setItem('pendingBillingCycle', billingCycle);

        showMessage('info', 'Redirecting to secure payment...');

        // Open payment gateway
        window.open(checkoutUrl, '_blank', 'noopener,noreferrer');

    } catch (error) {
        console.error('Payment initiation failed:', error);
        const errorMsg =
            error.response?.data?.message ||
            'Failed to start payment. Please try again.';
        showMessage('error', errorMsg);
    } finally {
        setPaymentProcessing(false);
    }
};


    const calculatePrice = (plan) => {
        if (billingCycle === 'monthly') return plan.price.monthly;
        if (billingCycle === 'quarterly') return plan.price.quarterly;
        return plan.price.yearly;
    };

    const calculateSavings = (plan) => {
        const monthly = plan.price.monthly;
        if (billingCycle === 'quarterly') {
            const fullPrice = monthly * 3;
            const savings = fullPrice - plan.price.quarterly;
            return savings > 0 ? `Save ₹${savings}` : null;
        }
        if (billingCycle === 'yearly') {
            const fullPrice = monthly * 12;
            const savings = fullPrice - plan.price.yearly;
            return savings > 0 ? `Save ₹${savings}` : null;
        }
        return null;
    };

    const getFeatureList = (features) => {
        const featureList = [];

        if (features.profileViews === -1) {
            featureList.push({ text: 'Unlimited Profile Views', icon: <FaInfinity />, highlight: true });
        } else if (features.profileViews > 0) {
            featureList.push({ text: `${features.profileViews} Profile Views`, icon: <FaEye /> });
        }

        if (features.canSendMessages) {
            featureList.push({ text: 'Unlimited Messaging', icon: <FaEnvelope />, highlight: true });
        }

        if (features.contactViewQuota === -1) {
            featureList.push({ text: 'Unlimited Contact Reveals', icon: <FaPhone />, highlight: true });
        } else if (features.contactViewQuota > 0) {
            featureList.push({ text: `${features.contactViewQuota} Contact Reveals`, icon: <FaPhone /> });
        }

        if (features.canSendFiles) {
            featureList.push({ text: 'File & Photo Sharing', icon: <FaGem /> });
        }

        if (features.canSendVoiceMessages) {
            featureList.push({ text: 'Voice Messages', icon: <FaPhone /> });
        }

        if (features.canReactToMessages) {
            featureList.push({ text: 'Message Reactions', icon: <FaHeart /> });
        }

        if (features.voiceCalls) {
            featureList.push({ text: 'Voice Calls', icon: <FaPhone />, highlight: true });
        }

        if (features.videoCalls) {
            featureList.push({ text: 'Video Calls', icon: <FaVideo />, highlight: true });
        }

        if (features.canSeeWhoViewedProfile) {
            featureList.push({ text: 'See Who Viewed You', icon: <FaEye /> });
        }

        if (features.canSeeWhoShortlisted) {
            featureList.push({ text: 'See Who Shortlisted You', icon: <FaHeart /> });
        }

        if (features.advancedSearch) {
            featureList.push({ text: 'Advanced Search Filters', icon: <FaSearch /> });
        }

        if (features.priorityListing) {
            featureList.push({ text: 'Priority Profile Listing', icon: <FaBolt />, highlight: true });
        }

        if (features.highlightedProfile) {
            featureList.push({ text: 'Highlighted Profile', icon: <FaStar />, highlight: true });
        }

        if (features.verifiedBadge) {
            featureList.push({ text: 'Verified Badge', icon: <FaShieldAlt /> });
        }

        if (features.hideAds) {
            featureList.push({ text: 'Ad-Free Experience', icon: <FaCheck /> });
        }

        if (features.prioritySupport) {
            featureList.push({ text: 'Priority Support', icon: <FaHeadset />, highlight: true });
        }

        if (features.dedicatedManager) {
            featureList.push({ text: 'Dedicated Relationship Manager', icon: <FaUserTie />, highlight: true });
        }

        if (features.horoscopeMatching) {
            featureList.push({ text: 'Horoscope Matching', icon: <FaStar /> });
        }

        if (features.aiJathagam) {
            featureList.push({ text: 'AI Jathagam Analysis', icon: <FaChartLine /> });
        }

        if (features.marketAccess) {
            featureList.push({ text: 'Market Access', icon: <FaGem /> });
        }

        return featureList;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading plans...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 relative">
            {/* Inline Messages */}
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-3">
                <AnimatePresence>
                    {messages.map(msg => (
                        <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: -50 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -50 }}
                            className={`px-6 py-3 rounded-full shadow-lg text-white font-medium text-sm flex items-center gap-3 ${msg.type === 'success' ? 'bg-green-600' :
                                msg.type === 'error' ? 'bg-red-600' :
                                    'bg-blue-600'
                                }`}
                        >
                            {msg.type === 'success' && <FaCheck />}
                            {msg.type === 'error' && <FaTimes />}
                            {msg.type === 'info' && <FaCrown />}
                            {msg.text}
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* Header */}
            <div className="text-center mb-12">
                <motion.h1
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-orange-500 bg-clip-text text-transparent mb-4"
                >
                    Choose Your Perfect Plan
                </motion.h1>
                <p className="text-gray-600 text-lg">
                    Find your life partner with our premium features
                </p>
            </div>

            {/* Current Plan Status */}
            {currentSubscription && currentSubscription.currentPlan !== 'free' && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8 p-6 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border-2 border-rose-200"
                >
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">
                                    Current Plan: {currentSubscription.planDetails?.displayName}
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {currentSubscription.isActive ? (
                                        <>
                                            Active until {new Date(currentSubscription.expiryDate).toLocaleDateString()}
                                        </>
                                    ) : (
                                        <span className="text-red-600">Expired</span>
                                    )}
                                </p>
                            </div>
                        </div>
                        {usageStats && (
                            <div className="flex gap-4">
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-rose-600">
                                        {usageStats.usageStats.profileViewsToday}
                                    </p>
                                    <p className="text-xs text-gray-600">Views Today</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-pink-600">
                                        {usageStats.usageStats.messagesSentToday}
                                    </p>
                                    <p className="text-xs text-gray-600">Messages Today</p>
                                </div>
                                <div className="text-center">
                                    <p className="text-2xl font-bold text-orange-600">
                                        {usageStats.usageStats.contactsRevealedTotal}
                                    </p>
                                    <p className="text-xs text-gray-600">Contacts Revealed</p>
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Billing Cycle Toggle */}
            <div className="flex justify-center mb-12">
                <div className="bg-gray-100 p-1.5 rounded-lg inline-flex border border-gray-200">
                    <button
                        onClick={() => setBillingCycle('monthly')}
                        className={`px-6 py-2 rounded-md text-sm font-semibold transition-all ${billingCycle === 'monthly'
                            ? 'bg-white text-black shadow-sm ring-1 ring-black/5'
                            : 'text-gray-500 hover:text-gray-900'
                            }`}
                    >
                        Monthly
                    </button>
                    <button
                        onClick={() => setBillingCycle('quarterly')}
                        className={`px-6 py-2 rounded-md text-sm font-semibold transition-all relative ${billingCycle === 'quarterly'
                            ? 'bg-white text-black shadow-sm ring-1 ring-black/5'
                            : 'text-gray-500 hover:text-gray-900'
                            }`}
                    >
                        Quarterly
                        <span className="absolute -top-2.5 -right-1 bg-green-100 text-green-700 text-[10px] font-bold px-1.5 py-0.5 rounded border border-green-200">
                            -10%
                        </span>
                    </button>
                    <button
                        onClick={() => setBillingCycle('yearly')}
                        className={`px-6 py-2 rounded-md text-sm font-semibold transition-all relative ${billingCycle === 'yearly'
                            ? 'bg-white text-black shadow-sm ring-1 ring-black/5'
                            : 'text-gray-500 hover:text-gray-900'
                            }`}
                    >
                        Yearly
                        <span className="absolute -top-2.5 -right-1 bg-orange-100 text-orange-700 text-[10px] font-bold px-1.5 py-0.5 rounded border border-orange-200">
                            -20%
                        </span>
                    </button>
                </div>
            </div>

            {/* Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                {plans.map((plan, index) => {
                    const isCurrentPlan = currentSubscription?.currentPlan === plan.name;
                    const featureList = getFeatureList(plan.features);
                    const price = calculatePrice(plan);
                    const savings = calculateSavings(plan);

                    return (
                        <motion.div
                            key={plan._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className={`relative rounded-3xl p-8 ${plan.popular
                                ? 'bg-gradient-to-br from-rose-600 to-pink-600 text-white shadow-2xl scale-105 border-4 border-yellow-400'
                                : 'bg-white border-2 border-gray-200 hover:border-rose-300 hover:shadow-xl'
                                } transition-all duration-300`}
                        >
                            {plan.popular && (
                                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-rose-900 px-4 py-1 rounded-full text-sm font-bold shadow-lg">
                                    ⭐ MOST POPULAR
                                </div>
                            )}

                            <div className="text-center mb-6">
                                <h3 className={`text-2xl font-bold mb-2 ${plan.popular ? 'text-white' : 'text-gray-800'}`}>
                                    {plan.displayName}
                                </h3>
                                <div className="flex items-baseline justify-center gap-2">
                                    <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-rose-600'}`}>
                                        ₹{price}
                                    </span>
                                    <span className={`text-sm ${plan.popular ? 'text-rose-100' : 'text-gray-600'}`}>
                                        /{billingCycle === 'yearly' ? 'year' : billingCycle === 'quarterly' ? 'quarter' : 'month'}
                                    </span>
                                </div>
                                {savings && (
                                    <p className={`text-sm mt-2 font-medium ${plan.popular ? 'text-yellow-300' : 'text-green-600'}`}>
                                        {savings}
                                    </p>
                                )}
                            </div>

                            <ul className="space-y-3 mb-8">
                                {featureList.slice(0, 10).map((feature, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                        <span className={`mt-1 ${feature.highlight ? 'text-yellow-400' : plan.popular ? 'text-rose-200' : 'text-rose-600'}`}>
                                            {feature.icon || <FaCheck />}
                                        </span>
                                        <span className={`text-sm ${plan.popular ? 'text-white' : 'text-gray-700'} ${feature.highlight ? 'font-bold' : ''}`}>
                                            {feature.text}
                                        </span>
                                    </li>
                                ))}
                                {featureList.length > 10 && (
                                    <li className={`text-sm italic ${plan.popular ? 'text-rose-200' : 'text-gray-600'}`}>
                                        +{featureList.length - 10} more features
                                    </li>
                                )}
                            </ul>

                            <button
                                onClick={() => handleSelectPlan(plan)}
                                disabled={isCurrentPlan && currentSubscription?.isActive}
                                className={`w-full py-3 rounded-xl font-bold transition-all ${isCurrentPlan && currentSubscription?.isActive
                                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                                    : plan.popular
                                        ? 'bg-white text-rose-600 hover:bg-yellow-400 hover:text-rose-900 shadow-lg hover:shadow-xl'
                                        : plan.name === 'free'
                                            ? 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                                            : 'bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:shadow-lg hover:scale-105'
                                    }`}
                            >
                                {isCurrentPlan && currentSubscription?.isActive ? (
                                    <>
                                        <FaCheck className="inline mr-2" />
                                        Current Plan
                                    </>
                                ) : plan.name === 'free' ? (
                                    'Free Forever'
                                ) : (
                                    <>
                                        <FaCrown className="inline mr-2" />
                                        Upgrade Now
                                    </>
                                )}
                            </button>
                        </motion.div>
                    );
                })}
            </div>

            {/* Feature Comparison Table */}
            <div className="bg-white rounded-3xl shadow-xl p-8 mb-12">
                <h2 className="text-3xl font-bold text-center mb-8 bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
                    Detailed Feature Comparison
                </h2>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b-2 border-gray-200">
                                <th className="text-left p-4 font-bold text-gray-800">Feature</th>
                                {plans.map(plan => (
                                    <th key={plan._id} className="p-4 text-center">
                                        <div className="font-bold text-gray-800">{plan.displayName}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <FeatureRow label="Profile Views" plans={plans} feature="profileViews" />
                            <FeatureRow label="Contact Reveals" plans={plans} feature="contactViewQuota" />
                            <FeatureRow label="Messaging" plans={plans} feature="canSendMessages" />
                            <FeatureRow label="File Sharing" plans={plans} feature="canSendFiles" />
                            <FeatureRow label="Voice Calls" plans={plans} feature="voiceCalls" />
                            <FeatureRow label="Video Calls" plans={plans} feature="videoCalls" />
                            <FeatureRow label="See Who Viewed" plans={plans} feature="canSeeWhoViewedProfile" />
                            <FeatureRow label="Priority Listing" plans={plans} feature="priorityListing" />
                            <FeatureRow label="Verified Badge" plans={plans} feature="verifiedBadge" />
                            <FeatureRow label="Ad-Free" plans={plans} feature="hideAds" />
                            <FeatureRow label="Priority Support" plans={plans} feature="prioritySupport" />
                            <FeatureRow label="Dedicated Manager" plans={plans} feature="dedicatedManager" />
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Payment Modal */}
            <AnimatePresence>
                {showPaymentModal && selectedPlan && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                        onClick={() => !paymentProcessing && setShowPaymentModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className="bg-white rounded-3xl p-8 max-w-md w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="text-center mb-6">
                                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                                    Subscribe to {selectedPlan.displayName}
                                </h3>
                                <div className="flex items-baseline justify-center gap-2">
                                    <span className="text-4xl font-bold text-rose-600">
                                        ₹{calculatePrice(selectedPlan)}
                                    </span>
                                    <span className="text-gray-600">
                                        /{billingCycle === 'yearly' ? 'year' : billingCycle === 'quarterly' ? 'quarter' : 'month'}
                                    </span>
                                </div>
                                {calculateSavings(selectedPlan) && (
                                    <p className="text-green-600 font-medium mt-2">
                                        {calculateSavings(selectedPlan)}
                                    </p>
                                )}
                            </div>

                            <div className="bg-gray-50 rounded-2xl p-6 mb-6">
                                <h4 className="font-bold text-gray-800 mb-4">What you'll get:</h4>
                                <ul className="space-y-2">
                                    {getFeatureList(selectedPlan.features).slice(0, 5).map((feature, idx) => (
                                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                                            <FaCheck className="text-green-600" />
                                            {feature.text}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowPaymentModal(false)}
                                    disabled={paymentProcessing}
                                    className="flex-1 px-6 py-3 border-2 border-gray-200 text-gray-600 font-bold rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubscribe}
                                    disabled={paymentProcessing}
                                    className="flex-1 px-6 py-3 bg-gradient-to-r from-rose-600 to-pink-600 text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                                >
                                    {paymentProcessing ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block mr-2"></div>
                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            <FaCrown className="inline mr-2" />
                                            Proceed to Payment
                                        </>
                                    )}
                                </button>
                            </div>

                            <p className="text-xs text-gray-500 text-center mt-4">
                                By subscribing, you agree to our Terms of Service and Privacy Policy
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const FeatureRow = ({ label, plans, feature }) => {
    const getValue = (plan) => {
        const value = plan.features[feature];
        if (typeof value === 'boolean') {
            return value ? <FaCheck className="text-green-600 mx-auto" /> : <FaTimes className="text-red-400 mx-auto" />;
        }
        if (value === -1) {
            return <span className="text-rose-600 font-bold">Unlimited</span>;
        }
        if (value === 0) {
            return <FaTimes className="text-red-400 mx-auto" />;
        }
        return <span className="text-gray-800 font-medium">{value}</span>;
    };

    return (
        <tr className="border-b border-gray-100 hover:bg-gray-50">
            <td className="p-4 font-medium text-gray-700">{label}</td>
            {plans.map(plan => (
                <td key={plan._id} className="p-4 text-center">
                    {getValue(plan)}
                </td>
            ))}
        </tr>
    );
};

export default Subscription;